import React, { useMemo } from 'react';
import classNames from 'classnames';
import { NDA_COPIES, NDA_ICON, NdaStatus } from 'models/Nda';
import {
  isNdaCounterSignerPending,
  isNdaNotSent,
  isNdaRejected,
  isNdaSent,
  isNdaSigned,
} from 'utils';
import LeadCardTag from '../LeadCardTag/LeadCardTag.component';
import TransWithNda from 'components/TransWithNda';
import { SHORT_MAX_CHARS } from 'components/TransWithNda/TransWithNda.component';

import './LeadCardTags.styles.scss';

export type LeadCardTagsProps = {
  className?: string;
  signingStatus: NdaStatus;
  isExternal: boolean;
  isPrivateViewer: boolean;
  isRequestedPrivateViewer: boolean;
  isShareableLinkConsumer: boolean;
  confirmedInterest: boolean;
  isOfferRejected: boolean;
  isOfferAccepted: boolean;
  isInactive: boolean;
  envelopeTemplateName: string;
};

const LeadCardTags: React.FC<LeadCardTagsProps> = (props) => {
  const {
    className,
    signingStatus,
    isExternal,
    isPrivateViewer,
    isRequestedPrivateViewer,
    isShareableLinkConsumer,
    confirmedInterest,
    isOfferRejected,
    isOfferAccepted,
    isInactive,
    envelopeTemplateName,
  } = props;

  const classes = classNames('lead-card-tags', className);

  const isEnquired = useMemo(
    () =>
      !isExternal &&
      !isPrivateViewer &&
      !isRequestedPrivateViewer &&
      !isShareableLinkConsumer,
    [
      isExternal,
      isPrivateViewer,
      isRequestedPrivateViewer,
      isShareableLinkConsumer,
    ],
  );

  const tags = useMemo(
    () => [
      {
        value: confirmedInterest,
        label: 'confirmed',
        success: true,
      },
      {
        value: isRequestedPrivateViewer,
        label: 'interested',
        info: true,
      },
      {
        value:
          !signingStatus || isNdaNotSent(signingStatus) ? null : signingStatus,
        label: (
          <TransWithNda
            i18nKey={NDA_COPIES[signingStatus]}
            maxChars={SHORT_MAX_CHARS}
            envelopeTemplateName={envelopeTemplateName}
          />
        ),
        alert: isNdaSent(signingStatus),
        success: isNdaSigned(signingStatus),
        danger: isNdaRejected(signingStatus),
        info: isNdaCounterSignerPending(signingStatus),
        Icon: NDA_ICON[signingStatus],
      },
      {
        value: isEnquired,
        label: 'enquired',
      },
      {
        value: isExternal,
        label: 'added',
      },
      {
        value: isPrivateViewer,
        label: 'invited',
      },
      {
        value: isShareableLinkConsumer,
        label: 'requestedLink',
      },
      {
        value: isOfferRejected,
        label: 'offerRejected',
        alert: true,
      },
      {
        value: isOfferAccepted,
        label: 'offerAccepted',
        success: true,
      },
      { value: isInactive, label: 'inactive', alert: true },
    ],
    [
      confirmedInterest,
      isExternal,
      isOfferAccepted,
      isOfferRejected,
      isPrivateViewer,
      isRequestedPrivateViewer,
      signingStatus,
      isInactive,
      isShareableLinkConsumer,
      isEnquired,
      envelopeTemplateName,
    ],
  );

  const RenderTags = useMemo(
    () =>
      tags
        .filter(({ value }) => value)
        .map(({ label, alert, success, danger, info, Icon }) => (
          <LeadCardTag
            key={String(label)}
            label={label}
            alert={alert}
            danger={danger}
            success={success}
            info={info}
            Icon={Icon}
          />
        )),
    [tags],
  );

  return <div className={classes}>{RenderTags}</div>;
};

export default LeadCardTags;
